import React, { Component, useEffect } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster, toast } from 'sonner';
import AppRouter from './routes';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import './App.css';

// Get Google Client ID from environment variable
// You'll need to create a .env file with REACT_APP_GOOGLE_CLIENT_ID=your_client_id
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Always wrap in GoogleOAuthProvider — useGoogleLogin hook crashes without it.
// When no real client ID is configured, pass a placeholder so the provider doesn't throw.
// Google auth buttons will simply fail silently (no popup) with a fake ID.
function GoogleOAuthWrapper({ children }: { children: ReactNode }) {
  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || 'not-configured'}>{children}</GoogleOAuthProvider>;
}

// Root error boundary — prevents React 19 from unmounting the entire tree on uncaught errors
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          fontFamily: 'system-ui, sans-serif',
          backgroundColor: '#f8fafc'
        }}>
          <h1 style={{ fontSize: '24px', color: '#1e293b', marginBottom: '12px' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#64748b', marginBottom: '16px', textAlign: 'center', maxWidth: '480px' }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px',
              backgroundColor: '#1e293b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  // Escape key dismisses all toasts (TOAST-04 accessibility)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toast.dismiss();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <ErrorBoundary>
      <GoogleOAuthWrapper>
        <AuthProvider>
          <NotificationProvider>
            <Toaster
              position="bottom-right"
              richColors
              closeButton
              duration={4000}
            />
            <AppRouter />
          </NotificationProvider>
        </AuthProvider>
      </GoogleOAuthWrapper>
    </ErrorBoundary>
  );
}

export default App;
